import { supabase } from '@/integrations/supabase/client';

interface InstallData {
  platform: string;
  browser: string;
  userAgent: string;
}

const detectPlatform = (): string => {
  const ua = navigator.userAgent;
  
  if (/iPad|iPhone|iPod/.test(ua)) {
    return 'iOS';
  }
  if (/Android/.test(ua)) {
    return 'Android';
  }
  if (/Windows/.test(ua)) {
    return 'Windows';
  }
  if (/Mac/.test(ua)) {
    return 'macOS';
  }
  if (/Linux/.test(ua)) {
    return 'Linux';
  }
  return 'Unknown';
};

const detectBrowser = (): string => {
  const ua = navigator.userAgent;
  
  if (/Edg/.test(ua)) {
    return 'Edge';
  }
  if (/Chrome/.test(ua)) {
    return 'Chrome';
  }
  if (/Firefox/.test(ua)) {
    return 'Firefox';
  }
  if (/Safari/.test(ua)) {
    return 'Safari';
  }
  if (/Opera|OPR/.test(ua)) {
    return 'Opera';
  }
  return 'Unknown';
};

export const trackPWAInstall = async (): Promise<boolean> => {
  try {
    const installData: InstallData = {
      platform: detectPlatform(),
      browser: detectBrowser(),
      userAgent: navigator.userAgent,
    };

    // Get current user if authenticated
    const { data: { user } } = await supabase.auth.getUser();

    // Note: IP address is NOT collected to protect user privacy
    // Only platform, browser, and anonymized user agent are stored
    const { error } = await supabase
      .from('pwa_installs')
      .insert({
        user_id: user?.id || null,
        platform: installData.platform,
        browser: installData.browser,
        // Store only browser family, not full user agent to reduce fingerprinting
        user_agent: installData.browser,
        // ip_address is intentionally NOT collected for privacy
      });

    if (error) {
      if (import.meta.env.DEV) {
        console.error('Error tracking PWA install:', error);
      }
      return false;
    }

    if (import.meta.env.DEV) {
      console.log('PWA install tracked successfully');
    }
    return true;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error tracking PWA install:', error);
    }
    return false;
  }
};

// Check if install was already tracked in this session
const INSTALL_TRACKED_KEY = 'pwa-install-tracked';

export const hasTrackedInstall = (): boolean => {
  return localStorage.getItem(INSTALL_TRACKED_KEY) === 'true';
};

export const markInstallTracked = (): void => {
  localStorage.setItem(INSTALL_TRACKED_KEY, 'true');
};
